// components/TransactionsTable.tsx
import React from 'react';
import { Transaction } from '../helpers/HelperInterfaces';
import { showLimitedCharacters } from '../helpers/HelperFunctions';

interface TransactionsTableProps {
  transactions: Transaction[];
  accountsMap: Record<string,string>;
  loading: boolean;
  error: string | null;
  showAll?: boolean;
  onSeeMore?: () => void;
}

/** Parse ISO date string as local time to avoid UTC off-by-one */
function formatDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'numeric',
    day:   'numeric',
    year:  'numeric',
  });
}

function formatAmount(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style:    'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(Math.abs(amount));
}

const TransactionsTable: React.FC<TransactionsTableProps> = ({
  transactions,
  accountsMap,
  loading,
  error,
  showAll = false,
  onSeeMore,
}) => {

  return (
    <div>
      {!showAll && <h2 className="section-title">Latest Transactions</h2>}

      <div className="transactions-card">
        {loading && (
          <div className="state-box"><div className="spinner" /> Loading…</div>
        )}
        {!loading && error && (
          <div className="state-box" style={{ color: 'rgba(231,76,60,0.8)' }}>{error}</div>
        )}
        {!loading && !error && transactions.length === 0 && (
          <div className="state-box">No transactions found.</div>
        )}
        {!loading && !error && transactions.length > 0 && (
          <>
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Account</th>
                  <th>Business</th>
                  <th>Type</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx, idx) => (
                  <tr key={`${tx.businessId}-${idx}`}>
                    <td className="tx-date">{formatDate(tx.createdDate)}</td>
                    <td>{showLimitedCharacters(tx.description)}</td>
                    <td>{showLimitedCharacters(accountsMap[tx.accountId])}</td>
                    <td className="tx-business">
                      {showLimitedCharacters(tx.businessName)}
                    </td>
                    <td>
                      <span className={`tx-type-badge ${tx.isExpense ? 'expense' : 'income'}`}>
                        {tx.isExpense ? 'Expense' : 'Income'}
                      </span>
                    </td>
                    <td className={`tx-amount ${tx.isExpense ? 'expense' : 'income'}`}>
                      {tx.isExpense ? '−' : '+'}{formatAmount(tx.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {!showAll && onSeeMore && (
              <div className="see-more-row">
                <button className="see-more-btn" onClick={onSeeMore}>
                  See more →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TransactionsTable;